import { c as _c } from "react/compiler-runtime";
import { memo } from "react";
import Image from "next/image";
import { t, SupportedLocale } from "@/lib/i18n";
import type { Product } from "@/data/products";
import { getProductImageThumbSrc, getProductImageAlt } from "@/lib/productImages";
import { getProductUrl } from "@/lib/urls";
import styles from "@/styles/Home.module.css";
type ProductCardProps = {
  product: Product;
  locale: SupportedLocale;
};

/**
 * Linki produktu używają zwykłego <a>, NIE next/link.
 *
 * W Next.js 16 + Pages Router + Turbopack client-side <Link> przy URL
 * `/en/{slug}` agresywnie strippuje `/en/` (heurystyka i18n, której nie
 * wyłącza nawet `locale={false}`). W efekcie prefetch i klik trafiają
 * na `/type-99b-...` (bez `/en/`), serwer odpowiada 200 (rewrite) + dev
 * `PageNotFoundError`, klient nie hyduje strony i wpada w pętlę requestów.
 *
 * Zwykły anchor wymusza pełną nawigację przeglądarki — żaden client router
 * nie modyfikuje URL. Strona produktu jest cache'owana na serwerze
 * (`x-nextjs-cache: HIT`), więc UX bez SPA jest praktycznie nieodróżnialny.
 */
export const ProductCard = memo(function ProductCard(t0) {
  const $ = _c(52);
  const {
    product,
    locale
  } = t0;
  const displayName = product.name ?? `12GO ${product.numbering}`;
  let t1;
  if ($[0] !== product.id) {
    t1 = getProductImageThumbSrc(product.id);
    $[0] = product.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const imageSrc = t1;
  let t2;
  if ($[2] !== product.name || $[3] !== product.numbering) {
    t2 = getProductImageAlt(product.name, product.numbering);
    $[2] = product.name;
    $[3] = product.numbering;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const imageAlt = t2;
  let t3;
  if ($[5] !== locale || $[6] !== product) {
    t3 = getProductUrl(product, locale);
    $[5] = locale;
    $[6] = product;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const productUrl = t3;
  let t4;
  if ($[8] !== displayName || $[9] !== locale) {
    t4 = t(locale, "product.seeDetailsTitle").replace("{name}", displayName);
    $[8] = displayName;
    $[9] = locale;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const linkTitle = t4;
  let t5;
  if ($[11] !== imageAlt || $[12] !== imageSrc) {
    t5 = <div className={styles.productImage}><Image src={imageSrc} alt={imageAlt} fill={true} sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 25vw" className={styles.productImageImg} unoptimized={true} /></div>;
    $[11] = imageAlt;
    $[12] = imageSrc;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== linkTitle || $[15] !== productUrl || $[16] !== t5) {
    t6 = <a href={productUrl} className={styles.productImageLink} title={linkTitle}>{t5}</a>;
    $[14] = linkTitle;
    $[15] = productUrl;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== displayName || $[19] !== linkTitle || $[20] !== productUrl) {
    t7 = <h3 className={styles.productTitle}><a href={productUrl} className={styles.productTitleLink} title={linkTitle}>{displayName}</a></h3>;
    $[18] = displayName;
    $[19] = linkTitle;
    $[20] = productUrl;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const t8 = `category.${product.category}`;
  let t9;
  if ($[22] !== locale || $[23] !== t8) {
    t9 = t(locale, t8);
    $[22] = locale;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t9) {
    t10 = <span className={styles.productTag}>{t9}</span>;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== locale || $[28] !== product.category || $[29] !== product.militarySubcategory) {
    t11 = product.category === "military" && product.militarySubcategory && <span className={styles.productTagSubcategory}>{t(locale, `militarySub.${product.militarySubcategory}`)}</span>;
    $[27] = locale;
    $[28] = product.category;
    $[29] = product.militarySubcategory;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== locale || $[32] !== product.category || $[33] !== product.faction) {
    t12 = product.category === "military" && product.faction && <span className={styles.productTagFaction}>{t(locale, `faction.${product.faction}`)}</span>;
    $[31] = locale;
    $[32] = product.category;
    $[33] = product.faction;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12) {
    t13 = <div className={styles.productTags}>{t10}{t11}{t12}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== locale) {
    t14 = t(locale, "product.seeDetails");
    $[39] = locale;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== linkTitle || $[42] !== productUrl || $[43] !== t14) {
    t15 = <a href={productUrl} className={styles.productCta} title={linkTitle}>{t14}</a>;
    $[41] = linkTitle;
    $[42] = productUrl;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t13 || $[46] !== t15 || $[47] !== t7) {
    t16 = <div className={styles.productBody}>{t7}{t13}{t15}</div>;
    $[45] = t13;
    $[46] = t15;
    $[47] = t7;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t16 || $[50] !== t6) {
    t17 = <article className={styles.productCard}>{t6}{t16}</article>;
    $[49] = t16;
    $[50] = t6;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  return t17;
});